import { BoxProps, Text } from '@chakra-ui/react'
import { ScalarContentNode } from '@helo/cms'
import {
  differenceInDays,
  differenceInHours,
  differenceInMonths,
  differenceInWeeks,
  isValid,
} from 'date-fns'
import { merge } from 'lodash'

import { InlineSkeleton } from '../../components/SkeletonLoader'
import { ConditionalTooltip } from '../../components/Tooltip/Tooltip'
import { convertToUtcDate } from '../../utils/dates'
import { valueTransformer } from '../../utils/valueFormatter'
import { NumberRenderer } from '../number/NumberRenderer'

export const DateRenderer = ({
  contentNode,
  isLoading,
  ...props
}: { isLoading?: boolean; contentNode: ScalarContentNode } & BoxProps) => {
  const mergedOptions = merge({}, {}, contentNode.state)
  const { size, dateIncludeTime, dateRelativeUnit, styles, _tooltip } =
    mergedOptions

  // Extract styles object if it exists and is an object
  const customStyles = styles && typeof styles === 'object' ? styles : {}

  const showSkeleton = !!isLoading

  if (dateRelativeUnit) {
    if (showSkeleton) {
      return (
        <Text size={size} {...props} {...customStyles}>
          <InlineSkeleton isLoading />
        </Text>
      )
    }

    const today = convertToUtcDate(new Date())
    const date = convertToUtcDate(new Date(contentNode.data as string))

    if (!isValid(date) || !isValid(today)) {
      return '-'
    }

    const relativeValue = () => {
      switch (dateRelativeUnit) {
        case 'hours':
          return differenceInHours(today, date)
        case 'days':
        case 'since':
          return differenceInDays(today, date)
        case 'weeks':
          return differenceInWeeks(today, date)
        case 'months':
          return differenceInMonths(today, date)
        case 'adaptive':
          return differenceInDays(today, date)
        case 'until':
          return differenceInDays(date, today)
        default:
          return null
      }
    }

    return (
      <NumberRenderer
        size={size}
        {...props}
        {...customStyles}
        contentNode={{ ...contentNode, data: relativeValue() }}
      />
    )
  }

  return (
    <ConditionalTooltip isDisabled={showSkeleton} tooltipOptions={_tooltip}>
      <Text size={size} {...props} {...customStyles}>
        <InlineSkeleton isLoading={showSkeleton}>
          {valueTransformer(contentNode.data, { dateIncludeTime })}
        </InlineSkeleton>
      </Text>
    </ConditionalTooltip>
  )
}
